import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Carousel, Spinner } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import './Landing.css';
import { apiUrl, assetUrl } from '../utils/apiUrl';


function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = { en: { movie: 'movies', series: 'series', music: 'music' }, es: { movie: 'peliculas', series: 'series', music: 'musica' } };
  const categoryPath = paths[lang][post.category] || paths[lang].movie;
  return `${base}/${categoryPath}/${post.slug}`;
}

/**
 * Carrusel reutilizable de noticias aleatorias según el idioma.
 * Se puede usar en cualquier página (post, landing, etc.).
 *
 * @param {number} [excludePostId] - ID del post a excluir (ej. el post actual)
 * @param {number} [limit=10] - Número de noticias a mostrar
 * @param {string} [titleKey='common.blog.mightAlsoLike'] - Clave i18n del título de la sección
 * @param {string} [className] - Clases CSS adicionales para el contenedor
 */
const MoreNewsCarousel = ({ excludePostId, limit = 10, titleKey = 'common.blog.mightAlsoLike', className = '' }) => {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'es';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('language', lang);
    params.set('limit', String(limit));
    params.set('order', 'random');
    if (excludePostId != null) params.set('excludeId', String(excludePostId));
    fetch(apiUrl(`/api/blog/posts?${params}`), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((data) => {
        if (!cancelled && Array.isArray(data.posts)) setPosts(data.posts);
      })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, limit, excludePostId]);

  if (loading) {
    return (
      <section className={`py-4 py-md-5 bg-light ${className}`.trim()}>
        <Container>
          <h2 className="text-center fw-bold mb-4">{t(titleKey, language)}</h2>
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className={`landing-news py-4 py-md-5 bg-light ${className}`.trim()}>
      <Container>
        <h2 className="text-center fw-bold mb-4">{t(titleKey, language)}</h2>
        <Carousel indicators controls className="landing-news-carousel">
          {posts.map((post) => (
            <Carousel.Item key={post.id}>
              <Link to={getPostUrl(post)} className="text-decoration-none text-dark d-block">
                <div className="row align-items-center g-0 bg-white rounded shadow-sm overflow-hidden">
                  <div className="col-md-5">
                    {post.featured_image ? (
                      <img
                        src={assetUrl(post.featured_image)}
                        alt=""
                        className="w-100 h-100 object-fit-cover"
                        style={{ minHeight: 220, maxHeight: 280, objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="bg-secondary d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
                        <span className="display-4 text-white opacity-50">📰</span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-7 p-4">
                    <h5 className="fw-bold mb-2">{post.title}</h5>
                    <p className="text-muted small mb-0 line-clamp-3">{post.excerpt || post.title}</p>
                    <span className="text-primary small mt-2 d-inline-block">{t('landing.latestNewsReadMore', language)} →</span>
                  </div>
                </div>
              </Link>
            </Carousel.Item>
          ))}
        </Carousel>
      </Container>
    </section>
  );
};

export default MoreNewsCarousel;
